

import { useEffect, useState, useRef } from "react";
import {
  Button,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Typography,
  Popover,
  Checkbox,
  Menu,
  MenuItem,
} from "@mui/material";
import { useMediaQuery, useTheme, Collapse, Divider } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Print as PrintIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { IndentListApi, ApproveIndent } from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { handlePrintIndent } from "../Indent/IndentPrint";
import { Chip } from "@mui/material";

pdfMake.vfs = pdfFonts.vfs;

const IndentList = () => {
  const [searchStatus, setSearchStatus] = useState("Pending");
  const [indents, setIndents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [searchIndent, setSearchIndent] = useState("");
  const [searchItem, setSearchItem] = useState("");
  const [searchUOM, setSearchUOM] = useState("");
  const [searchProject, setSearchProject] = useState("");

  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [indentToApprove, setIndentToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [showRemarkForIndentId, setShowRemarkForIndentId] = useState(null);
  const [actionType, setActionType] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedIndent, setExpandedIndent] = useState(null);

  const navigate = useNavigate();

  // ✅ NEW: Date Range States
  const dateInputRef = useRef(null);
  const [selectingDate, setSelectingDate] = useState(null);
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [dateRange, setDateRange] = useState("today"); // ✅ NEW
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  // ✳️ New local states for custom picker UI
const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
const [tempCustomDateRange, setTempCustomDateRange] = useState({ from: "", to: "" });

// ✅ Apply / Cancel helpers
const handleApplyCustomDateRange = () => {
  if (tempCustomDateRange.from && tempCustomDateRange.to) {
    setCustomDateRange({ ...tempCustomDateRange });
    setDateRange("custom");
    setDateMenuAnchor(null);
    setShowCustomDateInputs(false);
  } else {
    toast.warning("Please select both From and To dates");
  }
};
const handleReset = () => {
  setSearchStatus("Pending");
  setSearchIndent("");
  setSearchProject("");
  setDateRange("today");
  setCustomDateRange({ from: "", to: "" });
  setTempCustomDateRange({ from: "", to: "" });  // add
  setShowCustomDateInputs(false);                // add
};

const handleCancelCustomDateRange = () => {
  setTempCustomDateRange({ from: "", to: "" });
  setShowCustomDateInputs(false);
};
  // ✅ NEW: Get Date Range Display Function
  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  // ✅ NEW: Format Date Display Function
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // ✅ NEW: Handle Reset Function
  // const handleReset = () => {
  //   setSearchStatus("Pending");
  //   setSearchIndent("");
  //   setSearchProject("");
  //   setDateRange("today");
  //   setCustomDateRange({ from: "", to: "" });
  // };

  const fetchIndents = async (pageNum = 1, filters = {}) => {
    const res = await IndentListApi(pageNum, filters);
    if (pageNum === 1) {
      setIndents(res.results);
    } else {
      setIndents((prev) => [...prev, ...res.results]);
    }
    setHasMore(res.next !== null);
    setPage(pageNum);
  };

  // ✅ UPDATED: useEffect with date range dependency
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchIndents(1, {
        indent_no: searchIndent,
        project_name: searchProject?.project_name || searchProject || "",
        status: searchStatus,
        date_range: dateRange, // ✅ NEW
        from_date: customDateRange.from || "", // ✅ NEW
        to_date: customDateRange.to || "", // ✅ NEW
      });
    }, 500);

    return () => clearTimeout(handler);
  }, [searchIndent, searchProject, searchStatus, dateRange, customDateRange]); // ✅ Added dependencies

  // ✅ UPDATED: loadMore with filters
  const loadMore = () => {
    const filters = {
      indent_no: searchIndent,
      project_name: searchProject?.project_name || searchProject || "",
      status: searchStatus,
      date_range: dateRange,
      from_date: customDateRange.from || "",
      to_date: customDateRange.to || "",
    };
    fetchIndents(page + 1, filters);
    setPage((prev) => prev + 1);
  };

  const filteredIndents = indents.filter((indent) => {
    const projectMatch = searchProject
      ? indent?.project_name
        ?.toLowerCase()
        .includes(searchProject.toLowerCase())
      : true;

    const indentNoMatch = searchItem
      ? indent?.indent_no?.toLowerCase().includes(searchItem.toLowerCase())
      : true;

    const uomMatch = searchUOM
      ? indent.items?.some((item) =>
        item.uom?.toLowerCase().includes(searchUOM.toLowerCase())
      )
      : true;

    return projectMatch && indentNoMatch && uomMatch;
  });

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const handleApprove = async (indent) => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveIndent(
        indent.indent_id,
        approvedById,
        indent.status,
        remarkInput
      );

      setIndents((prev) =>
        prev.map((item) =>
          item.indent_id === indent.indent_id ? updated : item
        )
      );

      toast.success(
        updated.status
          ? "Indent approved successfully!"
          : "Indent unapproved successfully!"
      );
    } catch (error) {
      toast.error("Failed to toggle indent approval.");
    }
  };

 function formatOnlyDate(dateTimeString) {
  if (!dateTimeString) return "N/A";

  // Take only the date part (before T)
  const datePart = dateTimeString.split("T")[0];  // "2025-12-02"

  const [year, month, day] = datePart.split("-");

  return `${day}-${month}-${year}`;
}

  const userRole = localStorage.getItem("role");

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  return (
    <Box>
      <Box sx={{ mt: -3, pt: -3, mb: 1, width: "100%" }}>
        {/* ✅ DESKTOP VIEW */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >


          {/* Radio Buttons */}
          <Box>
            <input
              type="radio"
              id="pending"
              name="status"
              value="Pending"
              checked={searchStatus === "Pending"}
              onChange={(e) => setSearchStatus(e.target.value)}
              style={{
                marginLeft: "10px",
                width: "10px",
                height: "10px",
                verticalAlign: "middle",
                transform: "scale(0.9)",
              }}
            />
            <label htmlFor="pending" style={{ fontSize: "12px" }}>
              Pending
            </label>

            <input
              type="radio"
              id="approved"
              name="status"
              value="Approved"
              checked={searchStatus === "Approved"}
              onChange={(e) => setSearchStatus(e.target.value)}
              style={{
                marginLeft: "10px",
                width: "10px",
                height: "10px",
                verticalAlign: "middle",
                transform: "scale(0.9)",
              }}
            />
            <label htmlFor="approved" style={{ fontSize: "12px" }}>
              Approved
            </label>

            <input
              type="radio"
              id="rejected"
              name="status"
              value="Rejected"
              checked={searchStatus === "Rejected"}
              onChange={(e) => setSearchStatus(e.target.value)}
              style={{
                marginLeft: "10px",
                width: "10px",
                height: "10px",
                verticalAlign: "middle",
                transform: "scale(0.9)",
              }}
            />
            <label htmlFor="rejected" style={{ fontSize: "12px" }}>
              Rejected
            </label>
          </Box>

          {/* ✅ NEW: Date Range Dropdown */}
          <TextField
            label="Date Range"
            value={
              dateRange === "custom" &&
                customDateRange.from &&
                customDateRange.to
                ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                : dateRange === "today"
                  ? "Today"
                  : dateRange === "yesterday"
                    ? "Yesterday"
                    : dateRange === "this_week"
                      ? "This Week"

                      : dateRange === "this_month"
                        ? "This Month"

                        : "Select Date Range"
            }
            onClick={(e) => setDateMenuAnchor(e.currentTarget)}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton size="small" edge="end">
                  <ArrowDropDownIcon />
                </IconButton>
              ),
            }}
            sx={{
              cursor: "pointer",
              minWidth: 160,
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "4px 8px",
              },
              "& .MuiInputBase-root": {
                height: 30,
                borderRadius: 0,
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.7rem",
              },
            }}
          />

          <TextField
            label="Search by Indent No"
            value={searchIndent}
            onChange={(e) => setSearchIndent(e.target.value)}
            sx={{
              flex: "1 1 150px",
              mr: 2,
              "& .MuiInputBase-root": {
                height: 30,
                borderRadius: 0,
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.7rem",
                top: -5,
              },
            }}
          />

          <TextField
            label="Search by Project"
            value={searchProject}
            onChange={(e) => setSearchProject(e.target.value)}
            sx={{
              flex: "1 1 150px",
              mr: 2,
              "& .MuiInputBase-root": {
                height: 30,
                borderRadius: 0,
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.65rem",
                top: -5,
              },
            }}
          />

          {/* ✅ NEW: Reset Button */}


        </Box>

        {/* ✅ MOBILE VIEW */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
          }}
        >
          {/* ✅ NEW: Date Range Dropdown - Mobile */}


          {/* Radio Buttons - Mobile */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              mb: 1.5,
              p: 1,
              bgcolor: "#f5f5f5",
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <label
                style={{
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <input
                  type="radio"
                  name="status"
                  value="Pending"
                  checked={searchStatus === "Pending"}
                  onChange={(e) => setSearchStatus(e.target.value)}
                />
                Pending
              </label>

              <label
                style={{
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <input
                  type="radio"
                  name="status"
                  value="Approved"
                  checked={searchStatus === "Approved"}
                  onChange={(e) => setSearchStatus(e.target.value)}
                />
                Approved
              </label>

              <label
                style={{
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <input
                  type="radio"
                  name="status"
                  value="Rejected"
                  checked={searchStatus === "Rejected"}
                  onChange={(e) => setSearchStatus(e.target.value)}
                />
                Rejected
              </label>
            </Box>

            {/* ✅ NEW: Reset Button - Mobile */}

          </Box>

          <Box sx={{ px: 1, mb: 1.5 }}>
            <TextField
              fullWidth
              label="Date Range"
              value={
                dateRange === "custom" &&
                  customDateRange.from &&
                  customDateRange.to
                  ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                  : dateRange === "today"
                    ? "Today"
                    : dateRange === "yesterday"
                      ? "Yesterday"
                      : dateRange === "this_week"
                        ? "This Week"

                        : dateRange === "this_month"
                          ? "This Month"

                          : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                "& .MuiInputBase-root": {
                  height: 40,
                },
              }}
            />
          </Box>

          {/* Search Fields Grid - Mobile */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
              },
              gap: 1.5,
              px: 1,
            }}
          >
            <TextField
              label="Search by Indent No"
              value={searchIndent}
              onChange={(e) => setSearchIndent(e.target.value)}
              size="small"
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                },
              }}
            />

            <TextField
              label="Search by Project"
              value={searchProject}
              onChange={(e) => setSearchProject(e.target.value)}
              size="small"
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* ✅ NEW: Hidden Date Input for Custom Range */}
      <input
        ref={dateInputRef}
        type="date"
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          width: "1px",
          height: "1px",
        }}
        value={
          selectingDate === "from" ? customDateRange.from : customDateRange.to
        }
        min={selectingDate === "to" ? customDateRange.from : undefined}
        onChange={(e) => {
          const selectedDate = e.target.value;

          if (selectingDate === "from") {
            setCustomDateRange((prev) => ({ ...prev, from: selectedDate }));
            setSelectingDate("to");
            setTimeout(() => {
              dateInputRef.current?.showPicker();
            }, 100);
          } else if (selectingDate === "to") {
            setCustomDateRange((prev) => ({ ...prev, to: selectedDate }));
            setDateRange("custom");
            setSelectingDate(null);
            setDateMenuAnchor(null);
          }
        }}
      />

      {/* ✅ NEW: Date Range Menu */}
     {/*  ✅ Simplified Date Range Menu with inline Custom Range  */}
<Menu
  anchorEl={dateMenuAnchor}
  open={Boolean(dateMenuAnchor)}
  onClose={() => {
    setDateMenuAnchor(null);
    setShowCustomDateInputs(false);
    setTempCustomDateRange({ from: "", to: "" });
  }}
  PaperProps={{ sx: { width: 340, maxHeight: 500 } }}
>
  {[
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "this_week", label: "This Week" },
    { key: "this_month", label: "This Month" },
  ].map((opt) => (
    <MenuItem
      key={opt.key}
      onClick={() => {
        setDateRange(opt.key);
        setCustomDateRange({ from: "", to: "" });
        setDateMenuAnchor(null);
        setShowCustomDateInputs(false);
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <Typography variant="body2">{opt.label}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
          {getDateRangeDisplay(opt.key)}
        </Typography>
      </Box>
    </MenuItem>
  ))}

  <Divider />

  {/* ▾  Custom Range Collapsible  */}
  <MenuItem
    onClick={() => setShowCustomDateInputs((s) => !s)}
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}>
      Custom Date Range
    </Typography>
    {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
  </MenuItem>

  <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
    <Box
      sx={{ px: 2, py: 2, bgcolor: "#f5f5f5", borderTop: "1px solid #e0e0e0" }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Select Date Range
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          label="From Date"
          type="date"
          size="small"
          value={tempCustomDateRange.from}
          onChange={(e) =>
            setTempCustomDateRange((p) => ({ ...p, from: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: tempCustomDateRange.to || undefined }}
          sx={{
            flex: 1,
            "& .MuiInputBase-root": { height: 40, fontSize: "0.75rem" },
            "& .MuiInputLabel-root": { fontSize: "0.75rem" },
          }}
        />
        <TextField
          label="To Date"
          type="date"
          size="small"
          value={tempCustomDateRange.to}
          onChange={(e) =>
            setTempCustomDateRange((p) => ({ ...p, to: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: tempCustomDateRange.from || undefined }}
          sx={{
            flex: 1,
            "& .MuiInputBase-root": { height: 40, fontSize: "0.75rem" },
            "& .MuiInputLabel-root": { fontSize: "0.75rem" },
          }}
        />
      </Box>

      {tempCustomDateRange.from && tempCustomDateRange.to && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 1.5,
            p: 1,
            bgcolor: "#e8f5e9",
            borderRadius: 1,
            textAlign: "center",
            color: "#2e7d32",
            fontWeight: 500,
          }}
        >
          📅 {formatDateDisplay(tempCustomDateRange.from)} to 
          {formatDateDisplay(tempCustomDateRange.to)}
        </Typography>
      )}

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCancelCustomDateRange}
          sx={{ fontSize: "0.7rem", textTransform: "none", borderRadius: 1 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleApplyCustomDateRange}
          disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
          sx={{ fontSize: "0.7rem", textTransform: "none", borderRadius: 1 }}
        >
          Apply
        </Button>
      </Box>
    </Box>
  </Collapse>
</Menu>

      <Box id="print-section">
        <InfiniteScroll
          dataLength={indents.length}
          next={loadMore}
          hasMore={hasMore}
          loader={<h4>Loading...</h4>}
        >
          {/* 🔥 MOBILE VIEW - Expandable List */}
          {isMobile ? (
            <Box sx={{ bgcolor: "background.paper" }}>
              {filteredIndents.map((indent, index) => (
                <Box key={index}>
                  {/* Main Row - Always Visible */}
                  <Box
                    onClick={() =>
                      setExpandedIndent(
                        expandedIndent === indent.indent_id
                          ? null
                          : indent.indent_id
                      )
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      cursor: "pointer",
                      bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:active": {
                        bgcolor: "#e3f2fd",
                      },
                      borderLeft: `4px solid ${indent.status === "Approved"
                          ? "#4caf50"
                          : indent.status === "Rejected"
                            ? "#f44336"
                            : "#ff9800"
                        }`,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      {/* Indent Number & Status */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "#666",
                            minWidth: 25,
                          }}
                        >
                          #{index + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                            flex: 1,
                          }}
                        >
                          {indent.indent_no}
                        </Typography>
                      </Box>

                      {/* Project & Date */}
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "#666",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>
                          {indent.project_name?.substring(0, 25) || "N/A"}
                        </span>
                        <span style={{ color: "#999" }}>•</span>
                        <span style={{ fontSize: "0.7rem" }}>
                          {formatOnlyDate(indent.created_at)}
                        </span>
                      </Typography>
                    </Box>

                    {/* Expand Icon */}
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {expandedIndent === indent.indent_id ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  {/* Expanded Details */}
                  <Collapse
                    in={expandedIndent === indent.indent_id}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                        borderLeft: `4px solid ${indent.status === "Approved"
                            ? "#4caf50"
                            : indent.status === "Rejected"
                              ? "#f44336"
                              : "#ff9800"
                          }`,
                      }}
                    >
                      {/* Detailed Info - 2 Column Grid */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 0.8,
                          mb: 1,
                        }}
                      >
                        {/* Project Full */}
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            PROJECT
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "#333",
                            }}
                          >
                            {indent.project_name || "N/A"}
                          </Typography>
                        </Box>

                        {/* Division */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            DIVISION
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {indent.division_name || "N/A"}
                          </Typography>
                        </Box>

                        {/* Indent Type */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            INDENT TYPE
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {indent.indent_type || "N/A"}
                          </Typography>
                        </Box>

                        {/* Created By */}
                        {indent.name && (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              CREATED BY
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {indent.name}
                            </Typography>
                          </Box>
                        )}

                        {/* Approved By */}
                        {indent.approved_name && (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              APPROVED BY
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {indent.approved_name}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Remarks */}
                      {indent.remarks && (
                        <Box sx={{ mb: 0.8 }}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                              mb: 0.3,
                            }}
                          >
                            REMARKS
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#555",
                              fontStyle: "italic",
                              lineHeight: 1.3,
                            }}
                          >
                            {indent.remarks}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                      {/* Action Buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {/* Approve - Only Admin */}
                        {userRole != "Staff" && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              bgcolor:
                                indent.status === "Approved"
                                  ? "#e8f5e9"
                                  : indent.status === "Rejected"
                                    ? "#ffebee"
                                    : "#fff3e0",
                              px: 1,
                              py: 0.5,
                              borderRadius: "6px",
                              border: `1px solid ${indent.status === "Approved"
                                  ? "#4caf50"
                                  : indent.status === "Rejected"
                                    ? "#f44336"
                                    : "#ff9800"
                                }`,
                            }}
                          >
                            <Checkbox
                              checked={
                                indent.status === "Approved" ||
                                indent.status === "Rejected"
                              }
                              onChange={(e) => {
                                e.stopPropagation();
                                setApproveAnchorEl(e.currentTarget);
                                setIndentToApprove(indent);
                              }}
                              sx={{
                                size: "small",
                                padding: 0,
                                paddingLeft: 1,
                                paddingRight: 1,
                                transform: "scale(0.6)",

                                color:
                                  indent.status === "Approved"
                                    ? "#4caf50"
                                    : indent.status === "Rejected"
                                      ? "#f44336"
                                      : "#ff9800",
                                "&.Mui-checked": {
                                  color:
                                    indent.status === "Approved"
                                      ? "#4caf50"
                                      : "#f44336",
                                },
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                color:
                                  indent.status === "Approved"
                                    ? "#2e7d32"
                                    : indent.status === "Rejected"
                                      ? "#c62828"
                                      : "#f57c00",
                              }}
                            >
                              {indent.status === "Approved"
                                ? "Approved"
                                : indent.status === "Rejected"
                                  ? "Rejected"
                                  : "Approve"}
                            </Typography>
                          </Box>
                        )}

                        {/* Print Button */}
                        <Box sx={{ display: "flex", gap: 0.6 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrintIndent(indent);
                            }}
                            sx={{
                              bgcolor: "#e3f2fd",
                              borderRadius: "6px",
                              width: 32,
                              height: 32,
                              "&:hover": {
                                bgcolor: "#bbdefb",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s",
                            }}
                          >
                            <PrintIcon
                              sx={{
                                fontSize: "1rem",
                                color: "#1976d2",
                                transform: "scale(0.2)",
                              }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>

                  {/* Divider between items */}
                  <Divider />
                </Box>
              ))}
            </Box>
          ) : (
            /* 💻 DESKTOP VIEW - Table */
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: "88vh",
                overflow: "auto",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 2200,
                  "& td, & th": {
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid #ccc",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        backgroundColor: "#f5f5f5",
                        fontSize: "0.67rem",
                      },
                    }}
                  >
                    {userRole != "Staff" && (
                      <TableCell sx={{ padding: "0px" }}>Approve</TableCell>
                    )}

                    <TableCell>S. No.</TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                        minWidth: 50,
                      }}
                    >
                      Indent No.
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Project</TableCell>

                    <TableCell>Division</TableCell>
                    <TableCell>Indent Type</TableCell>
                    <TableCell>Created By</TableCell>

                    <TableCell>Approved By</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredIndents.map((indent, index) => (
                    <TableRow
                      hover
                      key={index}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                          "& td": {
                            backgroundColor: "#e3f2fd !important",
                          },
                        },
                        "& td": {
                          paddingTop: "2px !important",
                          paddingBottom: "2px !important",
                          padding: "0px",
                          fontSize: { xs: "0.6.5rem", sm: "0.7rem" },
                          whiteSpace: "nowrap",
                          color: (theme) =>
                            theme.palette.mode === "dark" ? "white" : "inherit",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "black" : "inherit",
                        },
                      }}
                    >
                      {userRole != "Staff" && (
                        <TableCell>
                          <Checkbox
                            checked={
                              indent.status === "Approved" ||
                              indent.status === "Rejected"
                            }
                            onChange={(e) => {
                              setApproveAnchorEl(e.currentTarget);
                              setIndentToApprove(indent);
                            }}
                            sx={{
                              padding: "0px !important",
                              paddingLeft: 1,
                              paddingRight: 1,
                              transform: "scale(0.6)",
                              color:
                                indent.status === "Approved"
                                  ? "green"
                                  : indent.status === "Rejected"
                                    ? "red"
                                    : "grey",
                              "&.Mui-checked": {
                                color:
                                  indent.status === "Approved"
                                    ? "green"
                                    : "red",
                              },
                            }}
                          />
                        </TableCell>
                      )}

                      <TableCell>{index + 1}</TableCell>

                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#fafafa",
                          zIndex: 1,
                          minWidth: 50,
                        }}
                      >
                        {indent.indent_no}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            indent.status === "Approved"
                              ? "Approved"
                              : indent.status === "Rejected"
                                ? "Rejected"
                                : "Pending"
                          }
                          color={
                            indent.status === "Approved"
                              ? "success"
                              : indent.status === "Rejected"
                                ? "error"
                                : "warning"
                          }
                          variant="outlined"
                          size="small"
                          sx={{
                            height: "10px",
                            transform: "scale(0.8)",
                            "& .MuiChip-label": {
                              padding: "0 6px",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>{indent.project_name}</TableCell>
                      <TableCell>{indent.division_name}</TableCell>
                      <TableCell>{indent.indent_type}</TableCell>

                      <TableCell>{indent.name}</TableCell>

                      <TableCell>{indent.approved_name}</TableCell>
                      <TableCell>{formatOnlyDate(indent.created_at)}</TableCell>

                      <TableCell>
                        <Tooltip title={indent.remarks} arrow>
                          <span>{truncateText(indent.remarks, 20)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        className="no-print"
                        sx={{
                          position: "sticky",
                          right: 0,
                          backgroundColor: "#fafafa",
                          zIndex: 2,
                        }}
                      >
                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          onClick={() => handlePrintIndent(indents[index])}
                          size="small"
                        >
                          <PrintIcon transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>

        <ToastContainer position="top-right" autoClose={3000} />

        <Popover
          open={Boolean(approveAnchorEl)}
          anchorEl={approveAnchorEl}
          onClose={() => {
            setApproveAnchorEl(null);
            setIndentToApprove(null);
            setRemarkInput("");
            setShowRemarkForIndentId(null);
            setActionType(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Box sx={{ p: 2, minWidth: 250 }}>
            {showRemarkForIndentId === indentToApprove?.indent_id ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Please enter remark before{" "}
                  {actionType === "Approved" ? "approving" : "rejecting"}:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={remarkInput}
                  onChange={(e) => setRemarkInput(e.target.value)}
                  placeholder="Enter remark"
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Button
                    color={actionType === "Approved" ? "success" : "error"}
                    size="small"
                    onClick={() => {
                      if (!remarkInput.trim()) {
                        toast.error("Remark is required!");
                        return;
                      }
                      handleApprove({
                        ...indentToApprove,
                        status: actionType,
                        remarks: remarkInput,
                      });
                      setApproveAnchorEl(null);
                      setIndentToApprove(null);
                      setRemarkInput("");
                      setShowRemarkForIndentId(null);
                      setActionType(null);
                    }}
                  >
                    Yes
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setApproveAnchorEl(null);
                      setIndentToApprove(null);
                      setRemarkInput("");
                      setShowRemarkForIndentId(null);
                      setActionType(null);
                    }}
                  >
                    No
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Do you want to approve or reject this indent?
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Button
                    color="success"
                    size="small"
                    onClick={() => {
                      setShowRemarkForIndentId(indentToApprove?.indent_id);
                      setActionType("Approved");
                    }}
                  >
                    Approve
                  </Button>
                  <Button
                    color="error"
                    size="small"
                    onClick={() => {
                      setShowRemarkForIndentId(indentToApprove?.indent_id);
                      setActionType("Rejected");
                    }}
                  >
                    Reject
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default IndentList;